/**
 * `usePopoverState` is a helper function that will provide props for
 * implementing the dialog and disclosure a11y patterns.
 */
import * as React from "react"
import useDisclosureState from "../useDisclosureState"
import useAllCallbacks from "../useAllCallbacks"

type InitialState = Partial<typeof defaultState> &
  Parameters<typeof useDisclosureState>[0]

const defaultState = {
  hiddenId: "popover",
  anchor: null as HTMLElement | null
}
/**
 *
 * @param initialState The initial value for state.
 */
const usePopoverState = (initialState: InitialState = defaultState) => {
  const [{ anchor, hiddenId, ...state }] = React.useState({
    ...defaultState,
    ...initialState
  })
  const {
    bind: { id },
    bindDisclosure,
    hide,
    visible,
    ...rest
  } = useDisclosureState({
    ...state,
    hiddenId
  })
  const [localAnchor, setLocalAnchor] = React.useState<HTMLElement | null>(
    anchor
  )

  const handleClick: ReturnType<
    typeof useDisclosureState
  >["bindDisclosure"]["onClick"] = event => {
    setLocalAnchor(event.currentTarget)
  }

  return {
    ...rest,
    bind: {
      id: id,
      visible: visible,
      anchor: anchor ?? localAnchor,
      hide,
      tabIndex: -1
    },
    bindDisclosure: {
      ...bindDisclosure,
      "aria-haspopup": true,
      onClick: useAllCallbacks(bindDisclosure.onClick, handleClick),
      visible
    },
    hide,
    setAnchor: setLocalAnchor,
    visible
  }
}

export default usePopoverState
